import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const useInput = ({
  debounce,
  initialValue,
  maxLength,
  multiline,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  resetsOnEmpty,
  validateFn,
}) => {
  const [value, setValue] = useState(initialValue);
  const [previousValue, setPreviousValue] = useState(initialValue);
  const [invalid, setInvalid] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);

  const debounced = useDebouncedCallback((event, value) => {
    setValue(value);
    onChange?.(event, value);
  }, debounce);

  const validate = (value) => {
    if (!validateFn) {
      return;
    }

    const message = validateFn(value);

    const result = {
      invalid: Boolean(message),
      message,
    };

    setInvalid(result.invalid);
    setValidationMessage(result.message);

    return result;
  };

  const inputChange = (event, changeValue) => {
    let value = changeValue ?? event?.target?.value;

    const shouldTrim = !isNaN(maxLength) && value?.length > maxLength;

    if (shouldTrim) {
      value = value?.substring(0, maxLength);
    }

    setValue(value);

    if (debounce) {
      debounced(event, value);
    } else {
      onChange?.(event, value);
    }
  };

  const inputBlur = (event, changeValue) => {
    let updatedValue = changeValue ?? event.target.value;
    if (resetsOnEmpty && value === '') {
      setValue(previousValue ?? '');
    }

    const validation = validateFn ? validate(updatedValue) : null;

    updatedValue = validation?.invalid ? (previousValue ?? '') : updatedValue;

    setValue(updatedValue);
    setPreviousValue(updatedValue);
    onBlur?.(event, updatedValue);
  };

  const inputFocus = (event) => {
    event.target.select?.();
    onFocus?.(event, event.target.value);
    setPreviousValue(value);
  };

  const inputKeyDown = (event) => {
    // Enter restores the focus-time value so a single-line field can be
    // abandoned with it. In a multiline field Enter is a newline.
    if (!multiline && event.key === 'Enter') {
      setValue(previousValue ?? '');
    }

    onKeyDown?.(event, value);
  };

  // If the initialValue has changed and it's not the same as the input value,
  // we expect this to be an upstream change and should update the input value
  // accordingly.
  useEffect(() => {
    if (JSON.stringify(initialValue) !== JSON.stringify(value)) {
      setValue(initialValue);
    }
  }, [JSON.stringify(initialValue)]);

  return {
    inputBlur,
    inputChange,
    inputFocus,
    inputKeyDown,
    invalid,
    validationMessage,
    value,
  };
};

export default useInput;
